import { useState, type ReactNode } from 'react'
import { trackUse } from '@/state/usage'
import { Check, MoreHorizontal, Trash2, type LucideIcon } from 'lucide-react'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { Button, IconButton, Sheet, cx } from '@/components/ui'

/**
 * How an action is drawn: a chip (the phone's line), a full-width row in the
 * sheet, or a cell of the wide toolbar.
 */
export type ActionAs = 'chip' | 'row' | 'tool'

/** The wide toolbar's groups, in order; Delete (danger) is always its own, last. */
export type ActionGroup = 'state' | 'ops' | 'people'
const GROUP_ORDER: ActionGroup[] = ['state', 'ops', 'people']
/** Where an item sits when it gives no `group`: by id, else with the list operations. */
const DEFAULT_GROUP: Record<string, ActionGroup> = {
  pin: 'state',
  remind: 'state',
  pause: 'state',
  resume: 'state',
  complete: 'state',
  reopen: 'state',
  reschedule: 'state',
  people: 'people',
  share: 'people',
  'share-with': 'people',
  'nudge-alfie': 'people',
  'nudge-members': 'people',
  'add-date': 'people',
  edit: 'people',
}

/**
 * One action in a header's ActionBar. Items are data so the bar can decide what
 * stays inline: `pin` items are the most useful (at most two on a phone), the
 * rest go behind the `…` sheet, and `tone: 'danger'` always goes last. An item
 * that owns its own sheet (Remind, Share with…, the nudges) gives `render`
 * and draws itself as a chip or a row with `ActionTrigger`.
 */
export type ActionItem = {
  id: string
  label: string
  icon?: LucideIcon
  pin?: boolean
  /** dropped before layout (state-dependent actions, owner-only ones) */
  hidden?: boolean
  /** a toggle that is on: Pinned, with a check in the sheet */
  pressed?: boolean
  disabled?: boolean
  tone?: 'danger'
  /** which cell of the wide toolbar it sits in (default: by id) */
  group?: ActionGroup
  testid?: string
  title?: string
  onSelect?: () => void
  /** ask first: the action becomes "<label>" + Keep, and `onSelect` runs on the first */
  confirm?: { label: string; testid?: string }
  render?: (ctx: { as: ActionAs }) => ReactNode
}

/** The phone line: below Tailwind's `md`, where the page is one column. */
export const PHONE_QUERY = '(max-width: 767px)'

/**
 * The chip or row for one action. Chips are 44px tall on a phone (design system
 * D3); Delete is the only coloured one, and in black and glass it is carried by
 * the icon and weight alone (D6). Hairline, one font, no shadow. `tool` is the
 * wide layout's cell: borderless in a hairline group, 32px (44px on a coarse
 * pointer), tinted only when pressed.
 */
export const ActionTrigger = ({
  as = 'chip',
  label,
  icon: Icon,
  pressed,
  tone,
  disabled,
  onClick,
  testid,
  title,
}: {
  as?: ActionAs
  label: string
  icon?: LucideIcon
  pressed?: boolean
  tone?: 'danger'
  disabled?: boolean
  onClick: () => void
  testid?: string
  title?: string
}) => {
  const Glyph = tone === 'danger' ? Trash2 : Icon
  if (as === 'row')
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={title}
        data-testid={testid}
        aria-pressed={pressed}
        className={cx(
          'flex w-full items-center gap-3 px-4 min-h-11 py-2 text-left text-[15px] hover:bg-raised disabled:opacity-40',
          tone === 'danger' ? 'act-danger' : pressed && 'font-semibold',
        )}
      >
        {Glyph ? <Glyph size={16} aria-hidden className="shrink-0" /> : <span className="w-4 shrink-0" />}
        <span className="flex-1 min-w-0 truncate">{label}</span>
        {pressed && <Check size={16} aria-hidden className="shrink-0" />}
      </button>
    )
  if (as === 'tool')
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={title}
        data-testid={testid}
        aria-pressed={pressed}
        className={cx(
          'act-tool inline-flex items-center gap-1.5 min-h-8 pointer-coarse:min-h-11 px-3 text-xs whitespace-nowrap border-l border-line bg-transparent hover:bg-raised disabled:opacity-40',
          tone === 'danger' ? 'act-danger' : pressed ? 'act-on font-semibold' : 'text-fg-2',
        )}
      >
        {Glyph && <Glyph size={14} aria-hidden />}
        {label}
      </button>
    )
  return (
    <Button
      variant={pressed ? 'chipOn' : 'chip'}
      className={cx('max-md:min-h-11!', tone === 'danger' && 'act-danger')}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      aria-pressed={pressed}
    >
      {Glyph && <Glyph size={14} aria-hidden />}
      {label}
    </Button>
  )
}

const dangerLast = (items: ActionItem[]) => [
  ...items.filter((i) => i.tone !== 'danger'),
  ...items.filter((i) => i.tone === 'danger'),
]

/**
 * The standard row of actions in a header (List, Task, Project, Page, Date,
 * Person). Wide: one quiet toolbar of hairline groups (state, operations, people, then Delete apart), every action inline. Phone (430 wide): one line with
 * the pinned actions and `…`, whose sheet holds the rest, Delete last.
 * `layout="rows"` draws everything as sheet rows in place (Page's Actions sheet).
 * `phone` overrides the viewport (tests, the gallery).
 */
export const ActionBar = ({
  items,
  label = 'Actions',
  testid,
  layout = 'auto',
  phone,
  className,
}: {
  items: ActionItem[]
  label?: string
  testid?: string
  layout?: 'auto' | 'rows'
  phone?: boolean
  className?: string
}) => {
  const media = useMediaQuery(PHONE_QUERY)
  const onPhone = phone ?? media
  const [more, setMore] = useState(false)
  const [confirming, setConfirming] = useState<string | null>(null)
  const shown = dangerLast(items.filter((i) => !i.hidden))

  const select = (i: ActionItem, closeSheet: boolean) => {
    if (i.confirm && confirming !== i.id) {
      setConfirming(i.id)
      return
    }
    setConfirming(null)
    if (closeSheet) setMore(false)
    trackUse(`action:actionbar:${i.id}`)
    i.onSelect?.()
  }

  const draw = (i: ActionItem, as: ActionAs) => {
    if (i.render)
      return (
        <div key={i.id} className="contents">
          {i.render({ as })}
        </div>
      )
    if (i.confirm && confirming === i.id)
      return (
        <div key={i.id} className="contents">
          <ActionTrigger
            as={as}
            tone="danger"
            label={i.confirm.label}
            testid={i.confirm.testid}
            onClick={() => select(i, as === 'row')}
          />
          <ActionTrigger
            as={as}
            label="Keep"
            onClick={() => setConfirming(null)}
            testid={`${i.testid}-keep`}
          />
        </div>
      )
    return (
      <ActionTrigger
        key={i.id}
        as={as}
        label={i.label}
        icon={i.icon}
        pressed={i.pressed}
        tone={i.tone}
        disabled={i.disabled}
        testid={i.testid}
        title={i.title}
        onClick={() => select(i, as === 'row')}
      />
    )
  }

  const rows = (list: ActionItem[]) => (
    <ul className="group-list">
      {list.map((i) => (
        <li key={i.id}>{draw(i, 'row')}</li>
      ))}
    </ul>
  )

  if (layout === 'rows') return <div data-testid={testid}>{rows(shown)}</div>

  if (!onPhone) {
    const groupOf = (i: ActionItem) => i.group ?? DEFAULT_GROUP[i.id] ?? 'ops'
    const cells = [
      ...GROUP_ORDER.map((g) => shown.filter((i) => i.tone !== 'danger' && groupOf(i) === g)),
      shown.filter((i) => i.tone === 'danger'),
    ].filter((c) => c.length > 0)
    return (
      <div
        className={cx('flex flex-wrap items-center gap-2', className)}
        role="group"
        aria-label={label}
        data-testid={testid}
      >
        {cells.map((cell, n) => (
          <div
            key={cell[0]?.id}
            className={cx(
              'overflow-hidden rounded-lg border border-line',
              n === cells.length - 1 && cell[0]?.tone === 'danger' && cells.length > 1 && 'md:ml-auto',
            )}
          >
            <div className="flex flex-wrap items-stretch -ml-px">{cell.map((i) => draw(i, 'tool'))}</div>
          </div>
        ))}
      </div>
    )
  }

  const calm = shown.filter((i) => i.tone !== 'danger')
  const pinned = calm.filter((i) => i.pin).slice(0, 2)
  const inline = pinned.length ? pinned : calm.slice(0, 1)
  const rest = shown.filter((i) => !inline.includes(i))
  return (
    <div
      className={cx('flex items-center gap-1.5 min-h-12', className)}
      role="group"
      aria-label={label}
      data-testid={testid}
    >
      {inline.map((i) => draw(i, 'chip'))}
      {rest.length > 0 && (
        <IconButton
          label="More actions"
          className="min-h-11 min-w-11 border border-line rounded-full!"
          onClick={() => setMore(true)}
          data-testid={testid ? `${testid}-more` : 'actions-more'}
        >
          <MoreHorizontal size={18} aria-hidden />
        </IconButton>
      )}
      <Sheet
        open={more}
        onClose={() => {
          setMore(false)
          setConfirming(null)
        }}
        title={label}
      >
        <div data-testid={testid ? `${testid}-sheet` : 'actions-sheet'}>{rows(rest)}</div>
      </Sheet>
    </div>
  )
}
